import { useCallback, useEffect, useMemo, useState } from 'react';
import { masterDataApi, type MasterOption } from '@/api/masterData.api';
import type { SelectOption } from '@/components/ui/Select';
import { useDebounce } from '@/hooks/useDebounce';
import {
  formatCountryOption,
  formatCurrencyOption,
  formatTimezoneOption,
} from '@/utils/masterOptionLabels';
import { showApiError } from '@/utils/apiError';

export type MasterOptionKind = 'countries' | 'currencies' | 'timezones';

const FORMATTERS: Record<MasterOptionKind, (option: MasterOption) => string> = {
  countries: formatCountryOption,
  currencies: formatCurrencyOption,
  timezones: formatTimezoneOption,
};

const LOOKUP: Record<
  MasterOptionKind,
  (code: string) => Promise<MasterOption>
> = {
  countries: masterDataApi.getCountry,
  currencies: masterDataApi.getCurrency,
  timezones: masterDataApi.getTimezone,
};

const SEARCH: Record<
  MasterOptionKind,
  (params: { search?: string; page?: number; size?: number }) => Promise<MasterOption[]>
> = {
  countries: masterDataApi.countries,
  currencies: masterDataApi.currencies,
  timezones: masterDataApi.timezones,
};

function toOptions(kind: MasterOptionKind, rows: MasterOption[]): SelectOption[] {
  const format = FORMATTERS[kind];
  return rows.map((row) => ({ value: row.code, label: format(row) }));
}

function mergeSelectedOption(options: SelectOption[], selected?: SelectOption | null): SelectOption[] {
  if (!selected) return options;
  if (options.some((opt) => opt.value === selected.value)) return options;
  return [selected, ...options];
}

/** Server-backed searchable options for master reference data (country, currency, timezone). */
export function useMasterOptionSearch(kind: MasterOptionKind, selectedValue = '', pageSize = 50) {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<SelectOption | null>(null);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  const loadRows = useCallback(
    async (search: string, silent = false) => {
      if (!silent) setSearching(true);
      try {
        const rows = await SEARCH[kind]({ search: search || undefined, page: 0, size: pageSize });
        setOptions(toOptions(kind, rows));
      } catch (err) {
        showApiError(err, `Failed to load ${kind}`);
      } finally {
        if (!silent) setSearching(false);
        setLoading(false);
      }
    },
    [kind, pageSize],
  );

  useEffect(() => {
    void loadRows(debouncedSearch);
  }, [debouncedSearch, loadRows]);

  useEffect(() => {
    if (!selectedValue) {
      setSelectedOption(null);
      return;
    }
    let cancelled = false;
    LOOKUP[kind](selectedValue)
      .then((row) => {
        if (cancelled) return;
        const option = { value: row.code, label: FORMATTERS[kind](row) };
        setSelectedOption(option);
        setOptions((prev) => mergeSelectedOption(prev, option));
      })
      .catch(() => {
        if (!cancelled) {
          setSelectedOption({ value: selectedValue, label: selectedValue });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [kind, selectedValue]);

  const mergedOptions = useMemo(
    () => mergeSelectedOption(options, selectedOption),
    [options, selectedOption],
  );

  return {
    options: mergedOptions,
    loading,
    searching,
    onSearchQuery: setSearchQuery,
  };
}
